/**
 * Marketplace API
 * Public endpoints for book discovery and purchase
 * These endpoints do not require authentication
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { host } from 'constants/apiconfig'
import { findTokenByLicenseKey } from 'modules/arplaybook/utils/studentTokenRegistry'

import type { IBookPricing, IDemoSettings, ICTASettings } from 'types/marketplace'

// ==========================================
// Request/Response Types
// ==========================================

/** Color palette for marketplace theme */
export interface IMarketplaceColorPalette {
    primary: string
    secondary: string
    accent: string
    background: string
    text: string
}

/** Marketplace settings from seller's profile */
export interface IMarketplaceSettings {
    title?: string
    description?: string
    featuredBookIds?: string[]
    heroBanner?: string
    socialLinks?: { platform: string; url: string }[]
}

/** Response from getMarketplaceTheme */
export interface IMarketplaceThemeResponse {
    theme: {
        logo: string | null
        colorPalette: IMarketplaceColorPalette
    }
    seller: {
        name: string
        marketplaceSettings?: IMarketplaceSettings
    } | null
}

/**
 * Public book info for marketplace listing
 */
export interface IMarketplaceBook {
    _id: string
    name: string
    description?: string
    coverImage?: string
    targetAge?: string
    tags?: string[]
    pricing?: IBookPricing
    demoSettings?: IDemoSettings
    ctaSettings?: ICTASettings
    totalPages: number
    categoryCount: number
    sellerId?: string
    /** Average rating (0-5) from users */
    rating?: number
    /** Number of ratings/reviews */
    reviewCount?: number
}

/**
 * Seller info for marketplace
 */
export interface IMarketplaceSeller {
    name: string
    bio?: string
    profileImage?: string
}

/**
 * Response from getPublicBookInfo
 */
export interface IPublicBookInfoResponse {
    book: IMarketplaceBook
    seller: IMarketplaceSeller | null
}

/**
 * Request to create a purchase order
 */
export interface ICreatePurchaseOrderRequest {
    bookId: string
    buyerEmail: string
    buyerName?: string
    buyerPhone?: string
}

/**
 * Response from createPurchaseOrder
 * For paid books: returns Razorpay order details
 * For free books: returns license directly (isFreeBook=true)
 */
export interface ICreatePurchaseOrderResponse {
    razorpayOrderId?: string
    purchaseId: string
    razorpayKeyId?: string
    amount?: number
    currency?: string
    bookName: string
    sellerName?: string
    // Free book fields
    success?: boolean
    isFreeBook?: boolean
    licenseKey?: string
    activationUrl?: string
}

/**
 * Request to verify purchase
 */
export interface IVerifyPurchaseRequest {
    purchaseId: string
    razorpay_payment_id: string
    razorpay_order_id: string
    razorpay_signature: string
}

/**
 * Response from verifyPurchase
 */
export interface IVerifyPurchaseResponse {
    success: boolean
    message: string
    licenseKey: string
    activationUrl: string
    bookName: string
}

/**
 * Request to submit a book rating
 */
export interface ISubmitBookRatingRequest {
    bookId: string
    licenseKey: string
    rating: number
    review?: string
}

/**
 * Response from submitBookRating
 */
export interface ISubmitBookRatingResponse {
    message: string
    rating: number
    averageRating: number
    totalRatings: number
}

/**
 * Request to get user's rating for a book
 */
export interface IGetUserBookRatingRequest {
    bookId: string
    licenseKey: string
}

/**
 * Response from getUserBookRating
 */
export interface IGetUserBookRatingResponse {
    rating: number | null
    review?: string
}

// ==========================================
// Cart Purchase Types
// ==========================================

export interface ICartLicense {
    bookId: string
    bookName: string
    licenseKey: string
    activationUrl: string
    amount?: number
}

export interface ICreateCartPurchaseOrderRequest {
    bookIds: string[]
    buyerEmail: string
    buyerName?: string
    buyerPhone?: string
}

export interface ICreateCartPurchaseOrderResponse {
    // Paid cart fields
    razorpayOrderId?: string
    cartId: string
    razorpayKeyId?: string
    amount?: number
    currency?: string
    books?: { bookId: string; name: string; amount: number }[]
    sellerName?: string
    // Free cart fields
    success?: boolean
    isFreeCart?: boolean
    licenses?: ICartLicense[]
}

export interface IVerifyCartPurchaseRequest {
    cartId: string
    razorpay_payment_id: string
    razorpay_order_id: string
    razorpay_signature: string
}

export interface IVerifyCartPurchaseResponse {
    success: boolean
    message: string
    licenses: ICartLicense[]
}

/**
 * Marketplace discovery filters
 */
export interface IMarketplaceFilters {
    page?: number
    limit?: number
    tags?: string[]
    priceRange?: {
        min?: number
        max?: number
    }
    targetAge?: string
    search?: string
}

/**
 * Response from marketplace discovery (list of books)
 */
export interface IMarketplaceDiscoveryResponse {
    books: IMarketplaceBook[]
    sellers: Record<string, IMarketplaceSeller>
    pagination: {
        total: number
        page: number
        limit: number
        totalPages: number
    }
}

// ==========================================
// Demo Content Types
// ==========================================

/** A single page in demo content */
export interface IDemoPage {
    word: string
    speechText: string
    audio?: string
    visualType?: string
    model?: string
    imageMedia?: { mobile?: string; tablet?: string; desktop?: string }
    videoMedia?: { mobile?: string; tablet?: string; desktop?: string }
    imageAlt?: string
    videoAutoPlay?: boolean
    videoLoop?: boolean
    videoMuted?: boolean
    description?: string
}

/** A category (section) in demo content */
export interface IDemoCategory {
    name: string
    pages: IDemoPage[]
}

/** Response from getBookDemoContent */
export interface IBookDemoContentResponse {
    book: {
        bookId: string
        name: string
        categories: IDemoCategory[]
        pricing?: IBookPricing
        demoSettings?: IDemoSettings
        ctaSettings?: ICTASettings
        coverImage?: string
        description?: string
        welcomeTagline?: string
    }
}

// ==========================================
// Marketplace API (Public - No Auth)
// ==========================================

export const marketplaceApi = createApi({
    reducerPath: 'marketplaceApi',
    baseQuery: fetchBaseQuery({
        baseUrl: host,
    }),
    tagTypes: ['MarketplaceBooks', 'MarketplaceTheme'],
    endpoints: builder => ({
        /**
         * Get marketplace theme (seller branding)
         * Public endpoint - uses domain-based resolution
         */
        getMarketplaceTheme: builder.query<IMarketplaceThemeResponse, void>({
            query: () => 'ar-playbook/marketplace/theme',
            providesTags: ['MarketplaceTheme'],
        }),

        /**
         * Get all published books for marketplace discovery
         */
        getPublishedBooks: builder.query<IMarketplaceDiscoveryResponse, IMarketplaceFilters | void>(
            {
                query: filters => {
                    const params = new URLSearchParams()
                    if (filters?.page) {
                        params.set('page', filters.page.toString())
                    }
                    if (filters?.limit) {
                        params.set('limit', filters.limit.toString())
                    }
                    if (filters?.search) {
                        params.set('search', filters.search)
                    }
                    if (filters?.targetAge) {
                        params.set('targetAge', filters.targetAge)
                    }
                    if (filters?.tags?.length) {
                        params.set('tags', filters.tags.join(','))
                    }
                    if (filters?.priceRange?.min) {
                        params.set('minPrice', filters.priceRange.min.toString())
                    }
                    if (filters?.priceRange?.max) {
                        params.set('maxPrice', filters.priceRange.max.toString())
                    }

                    const queryString = params.toString()
                    return `ar-playbook/marketplace/books${queryString ? `?${queryString}` : ''}`
                },
                providesTags: ['MarketplaceBooks'],
            },
        ),

        /**
         * Get public book info for purchase page
         */
        getPublicBookInfo: builder.query<IPublicBookInfoResponse, string>({
            query: bookId => `ar-playbook/marketplace/books/${bookId}`,
            providesTags: (_result, _error, bookId) => [{ type: 'MarketplaceBooks', id: bookId }],
        }),

        /**
         * Get demo content for a book (public, no auth required)
         */
        getBookDemoContent: builder.query<IBookDemoContentResponse, string>({
            query: bookId => `ar-playbook/marketplace/books/${bookId}/demo`,
        }),

        /**
         * Create a Razorpay purchase order
         */
        createPurchaseOrder: builder.mutation<
            ICreatePurchaseOrderResponse,
            ICreatePurchaseOrderRequest
        >({
            query: ({ bookId, ...body }) => ({
                url: `ar-playbook/marketplace/books/${bookId}/purchase`,
                method: 'POST',
                body,
            }),
        }),

        /**
         * Verify payment and get license
         */
        verifyPurchase: builder.mutation<IVerifyPurchaseResponse, IVerifyPurchaseRequest>({
            query: ({ purchaseId, ...body }) => ({
                url: `ar-playbook/marketplace/purchases/${purchaseId}/verify`,
                method: 'POST',
                body,
            }),
        }),

        /**
         * Submit or update a book rating
         * Tries to use JWT token if the user has activated their license.
         * Falls back to sending license key in body for users who haven't activated yet.
         */
        submitBookRating: builder.mutation<ISubmitBookRatingResponse, ISubmitBookRatingRequest>({
            query: ({ bookId, licenseKey, ...body }) => {
                // Try to find JWT token for this license
                const token = findTokenByLicenseKey(licenseKey)

                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                }

                // If we have a JWT token, use it for auth
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`
                }

                return {
                    url: `ar-playbook/marketplace/books/${bookId}/rating`,
                    method: 'POST',
                    // Always include licenseKey in body for backward compatibility
                    body: { ...body, licenseKey },
                    headers,
                }
            },
            // Invalidate the book list to refresh ratings
            invalidatesTags: ['MarketplaceBooks'],
        }),

        /**
         * Get user's existing rating for a book
         * Tries to use JWT token if the user has activated their license.
         * Falls back to sending license key in query for users who haven't activated yet.
         */
        getUserBookRating: builder.query<IGetUserBookRatingResponse, IGetUserBookRatingRequest>({
            query: ({ bookId, licenseKey }) => {
                // Try to find JWT token for this license
                const token = findTokenByLicenseKey(licenseKey)

                const headers: Record<string, string> = {}

                // If we have a JWT token, use it for auth
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`
                }

                return {
                    url: `ar-playbook/marketplace/books/${bookId}/my-rating`,
                    // Always include licenseKey for backward compatibility
                    params: { licenseKey },
                    headers,
                }
            },
        }),

        /**
         * Create a Razorpay order for multiple books (cart purchase)
         */
        createCartPurchaseOrder: builder.mutation<
            ICreateCartPurchaseOrderResponse,
            ICreateCartPurchaseOrderRequest
        >({
            query: body => ({
                url: 'ar-playbook/marketplace/cart/purchase',
                method: 'POST',
                body,
            }),
        }),

        /**
         * Verify cart payment and get licenses for all books
         */
        verifyCartPurchase: builder.mutation<
            IVerifyCartPurchaseResponse,
            IVerifyCartPurchaseRequest
        >({
            query: ({ cartId, ...body }) => ({
                url: `ar-playbook/marketplace/cart/${cartId}/verify`,
                method: 'POST',
                body,
            }),
        }),
    }),
})

// Export hooks for usage in components
export const {
    useGetMarketplaceThemeQuery,
    useGetPublishedBooksQuery,
    useGetPublicBookInfoQuery,
    useCreatePurchaseOrderMutation,
    useVerifyPurchaseMutation,
    useSubmitBookRatingMutation,
    useGetUserBookRatingQuery,
    useLazyGetUserBookRatingQuery,
    useLazyGetBookDemoContentQuery,
    useCreateCartPurchaseOrderMutation,
    useVerifyCartPurchaseMutation,
} = marketplaceApi
